<template>
  <div class="gs-app gm-suppressions gm-refresh-tables">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>

    <!-- Navigation Tabs -->
    <navigation-tabs
      class="gm-nav-suppresion-tabs absolute"
      :nav_tabs="navTabs"
    >
    </navigation-tabs>

    <div class="gm-suppressions__workspace flex mb-4">
      <div class="w-full">
        <div class="gm-suppressions__surface flex flex-col w-full relative">
          <div
            class="w-full left-0 absolute groovemail-main-suppresors-tabs"
          >
            <div
            class="gm-suppressions__tabs flex bg-white main-suppresors-tabs-inner border-b-2 border-grey-200"
            >
              <gd-tabs
                :tabs="suppressionsTabs"
                :current-tab="suppressionsTab"
                :tab-active-class="'p-2 border-primary border-b-2 text-primary gs-default-tabs-item-active'"
                :line-class="'gs-default-tabs-active-line'"
                class="mail_suppressions_tab_head mx-6"
                @onClick="handleSuppressionsTabChange"
              />
            </div>
          </div>
          <div>
            <div
              v-if="suppressionsTab === 'unsubscribes'"
              class="w-full flex flex-col relative"
            >
              <Unsubscribers
                @showLoader="showLoader()"
                @hideLoader="hideLoader()"
              ></Unsubscribers>
            </div>
            <div
              v-if="suppressionsTab === 'complainers'"
              class="w-full flex flex-col relative"
            >
              <Complainers
                @showLoader="showLoader()"
                @hideLoader="hideLoader()"
              ></Complainers>
            </div>
            <div
              v-if="suppressionsTab === 'suppressions'"
              class="w-full flex flex-col relative"
            >
              <Suppresors
                @showLoader="showLoader()"
                @hideLoader="hideLoader()"
              ></Suppresors>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import auth from '@groovepages/gd-lib/auth';
import Unsubscribers from './unsubscribers/unsubscribers.vue';
import Complainers from './complainers/complainers.vue';
import Suppresors from './suppressors/suppressors.vue';
import NavigationTabs from '@/components/navigation-tabs.vue';

export default {
  middleware: 'auth',
  metaInfo() {
    return {
      title: this.$t('frontend_groovemail_components_sidebar_suppressions'),
    };
  },
  data() {
    return {
      suppressionsTab: 'unsubscribes',
      isLoading: false,
    };
  },

  components: {
    Unsubscribers,
    Complainers,
    Suppresors,
    NavigationTabs,
  },

  methods: {
    // getters from store.
    ...mapGetters('mailing', ['getCanImportLeads']),

    // show the loader
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide the loader
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    /**
     * Reloads the page on suppression tab change.
     * @param {string} newTab new suppression tab
     */
    handleSuppressionsTabChange(newTab) {
      this.suppressionsTab = newTab;
      this.$router.replace({
        name: 'Suppressions',
        query: { type: this.suppressionsTab },
      });
    },
  },

  // Initializes navigation tabs and currently selected suppression tab.
  async created() {
    // show the loader
    this.showLoader();
    
    this.suppressionsTab = this.$route.query.type;
  },

  computed: {
    /**
     * Returns navigation tabs data for Suppressions
     * @return {array} navigation tabs data
    */
    navTabs() {
      return [
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_leads',
          tabRoute: 'Leads2',
        },

        // check if the user can import leads
        this.userCanImportLeads === 1 && this.canImportLeads ? {
          tabName: 'frontend_groovemail_imported_leads_listing_page_imports_nav',
          tabRoute: 'ImportedLeadsListing',
        } : '',

        {
          tabName: 'frontend_groovemail_components_sidebar_suppressions',
          tabRoute: 'Suppressions',
          tabQuery: { type: 'unsubscribes' },
        }
      ];
    },
    /**
     * Returns a number which determines if user can import contacts or not based on admin permissions.
     * @return {number} if 1 - user can import contacts
     *                  if 0 - user cannot import contacts
     */
    userCanImportLeads() {
      return this.getCanImportLeads();
    },

    /**
     * Determines whether a user can import contacts or not based on plan of the user.
     * @return {boolean} if true - user can import contacts
     *                  if false - user cannot import contacts
     */
    canImportLeads() {
      return auth.fetchFeatureValue('groovemail_can_import_contacts') === 1;
    },

    /**
     * Returns suppression tabs data.
     * @return {array} - array of suppression tabs
     */
    suppressionsTabs() {
      return [
        {
          title: `<span class="text-sm font-semibold">&nbsp;${this.$t(
            'frontend_groovemail_components_suppressions_unsubscribers'
          )} <span>`,
          value: 'unsubscribes',
        },
        {
          title: `<span class="text-sm font-semibold">&nbsp;${this.$t(
            'frontend_groovemail_components_suppressions_complainers'
          )} <span>`,
          value: 'complainers',
        },
        {
          title: `<span class="text-sm font-semibold">&nbsp;${this.$t(
            'frontend_groovemail_suppressions_route_page_title'
          )} <span>`,
          value: 'suppressions',
        },
      ];
    },
  },
};
</script>

<style lang="scss">
.groovemail-suppresors-table,
.groovemail-unsubscribers-table,
.groovemail-camplainers-table {
  .VueTables__table {
    overflow: auto !important;
  }
  .VueTables--server table {
    th:nth-child(1),
    td:nth-child(1),
    th:nth-child(2),
    td:nth-child(2) {
      width: 35%;
    }
  }
}

.groovemail-camplainers-table,
.groovemail-unsubscribers-table {
  .VueTables__search-field {
    margin-bottom: 150px !important;
  }
}

.groovemail-suppresors-table {
  .VueTables__search-field {
    margin-bottom: 200px !important;
  }
}

.mail_suppressions_tab_head {
  button {
    margin-right: 8px;
    padding: 16px;
    outline: none;
    color: var(--gs-muted-foreground);
    font-size: 14px;
    font-weight: 600;
    transition: color 120ms ease;
    span {
      outline: none;
    }

    &:hover {
      color: var(--gs-foreground);
    }
    
    &:focus {
      outline: none;
    }

    &.gs-default-tabs-item-active {
      color: var(--gs-primary);
    }
  }
}

.unnsubscribers_create_tab_head {
  button {
    margin-right: 8px;
    padding: 15px 10px;
    outline: none;
    span {
      outline: none;
    }
    
    &:focus {
      outline: none;
    }
  }
}

.groovemail-main-suppresors-tabs {
  z-index: 9;
  top: 160px;
  .main-suppresors-tabs-inner {
    border-radius: var(--gs-radius-lg) var(--gs-radius-lg) 0 0;
  }
}

.groovemail-suppresors-tabs {
  z-index: 9;
  top: 220px;
}

.gm-nav-suppresion-tabs {
  top: 37px;
  z-index: 10;
}
</style>

<style scoped>
.gm-suppressions {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-suppressions__workspace,
.gm-suppressions__surface {
  min-width: 0;
}

.gm-suppressions__surface {
  max-width: 1280px;
  margin: 0 auto;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-suppressions__tabs {
  border-top-left-radius: var(--gs-radius-lg);
  border-top-right-radius: var(--gs-radius-lg);
  border-bottom-color: var(--gs-border);
}

@media (max-width: 640px) {
  .gm-suppressions {
    padding: 16px;
  }

  .gm-suppressions__surface {
    border-radius: var(--gs-radius-md);
  }

  .gm-suppressions__tabs {
    overflow-x: auto;
    border-top-left-radius: var(--gs-radius-md);
    border-top-right-radius: var(--gs-radius-md);
  }
}
</style>
